import { Box, Stack, StackProps, Text } from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import { Trans, useTranslation } from 'react-i18next';
import { ErrorOption } from 'react-hook-form/dist/types/errors';
import { FileDropzone } from '../../../components/shared';
import { ExtAttachmentType } from '../../../api/extForms';

export type FilePickerProps = StackProps & {
  setFile: (file: FileWithPath) => void;
  offerFile: ExtAttachmentType;
  acceptText?: string;
  maxSizeInKB?: number;
  setError?: (error: ErrorOption) => void;
  error?: string;
};

/**
 * FilePicker component
 * @private
 *
 * @param setFile
 * @param offerFile
 * @param acceptText
 * @param error
 * @param setError
 * @param maxSizeInKB
 * @param stackProps
 */
export const FilePicker: React.FC<FilePickerProps> = ({
  setFile,
  offerFile,
  error = '',
  setError,
  acceptText = '.PDF, .PNG, .JPG.',
  maxSizeInKB = 5120,
  ...stackProps
}) => {
  const { t } = useTranslation();

  return (
    <Stack spacing="sm" {...stackProps}>
      <Stack spacing={0}>
        <Text fz="lg" fw={500}>
          <Trans
            i18nKey={
              offerFile.required
                ? 'common:requiredLabel'
                : 'common:optionalLabel'
            }
            values={{ label: offerFile.name }}
            components={{ text: <span style={{ fontWeight: 400 }} /> }}
          />
        </Text>
        <Text fz="md" fw={400}>
          {offerFile.description}
        </Text>
      </Stack>
      <Box>
        <FileDropzone
          error={error ? t(error) : ''}
          setError={value => {
            setError?.({
              type: 'fileDropzone',
              message: typeof value === 'function' ? value(error) : value,
            });
          }}
          onFileDrop={setFile}
          isLoading={false}
          accept={undefined}
          acceptText={acceptText}
          additionalText={t('common:maxFileSize', {
            value: maxSizeInKB,
            fromUnit: 'kB',
          })}
          maxSizeInKB={maxSizeInKB}
        />
      </Box>
    </Stack>
  );
};
